<script lang="ts">
  // External libs
  import { _ } from 'svelte-i18n';
  import { navigate } from 'svelte-navigator';
  import { debugLog } from '../../../lib/debug-log';

  // Local utils
  import { handleFetchRetry } from '../../handleFetchRetry';
  import { identifyDealership } from '../../../lib/services/pdf/identifyDealership';
  import { getStoredReferer } from '../../../utils/utmTracking';

  // Components
  import DealerDropdown from '../purchase-information/dealer-dropdown.svelte';

  let URL = `${process.env.SVELTE_APP_FE_REMOTE_URL}/api/mbd140`;
  const ALLOWED_IBAN_COUNTRIES = ['DE', 'CH', 'AT', 'FR', 'LU', 'BE', 'NL'];

  let showDealershipDropdown = false;

  try {
    if (process.env.SVELTE_APP_SHOW_DEALERSHIP_DROPDOWN === 'true') {
      showDealershipDropdown = true;
    }
  } catch (e) {
    debugLog({ e });
  }

  let recognizedText: any = {};
  let dealershipInfo: any;
  let receipt: any = {};
  let pdfParserResponse: any = {};

  if (localStorage.getItem('recognizedText') !== null) {
    recognizedText = JSON.parse(
      localStorage.getItem('recognizedText') as string,
    );

    receipt = JSON.parse(localStorage.getItem('receipt') as string);
  }

  // New flow: use recognizedText body directly

  const serverPayload = recognizedText;
  const response = serverPayload?.extractedData || {};
  const pdfData = serverPayload?.extractedData || {};

  let purchasePaybackId: string;

  // read from localstorage
  if (localStorage.getItem('purchasePayBack') !== null) {
    purchasePaybackId = JSON.parse(
      localStorage.getItem('purchasePayBack') as string,
    ).id;
  }

  const customer = response?.CustomerName || pdfData?.CustomerName || '';
  const dealer = response?.DealershipName || pdfData?.DealershipName || '';

  const zipCode = response?.DealershipAddress?.zipCode || pdfData?.DealershipAddress?.zipCode || '';

  const dealershipAddress = response?.DealershipAddress ? `${response?.DealershipAddress?.street ?? ''}, ${response?.DealershipAddress?.zipCode ?? ''} ${response?.DealershipAddress?.city ?? ''}`.trim() : (pdfData?.DealershipAddress ? `${pdfData?.DealershipAddress?.street ?? ''}, ${pdfData?.DealershipAddress?.zipCode ?? ''} ${pdfData?.DealershipAddress?.city ?? ''}`.trim() : '');

  const dealershipLocation =
    response?.DealershipLocation || pdfData?.DealershipLocation || dealershipAddress|| '';

    debugLog({ dealer, dealershipLocation, dealershipAddress });
  let match: any = {};

  let form: HTMLFormElement;
  let ibanInput: HTMLInputElement;
  let bankDetails: {
    bankCode: string;
    name: string;
    bic: string;
  } | null = null;

  const parseCustomerInfo = (pdfData: any) => {
    if (!pdfData) return {};    
    // Extract name - prioritize CustomerName over ContactPerson
    // only use ContactPerson if CustomerCompanyName is empty / otherwise the name is always filled with the ContactPerson which didn't give good results
    // let name = pdfData.CustomerName || pdfData.ContactPerson || '';
    let name =
      pdfData.CustomerName ||
      (localStorage.getItem('personalInformation') ? JSON.parse(localStorage.getItem('personalInformation') ?? '{}').invitationName : '') ||
      (pdfData.CustomerCompanyName ? '' : pdfData.ContactPerson || '');
    let company = pdfData.CustomerCompanyName || '';

    // Check if there's a company in the name (usually separated by " - " or similar)
    if (name && name.includes(' - ')) {
      const parts = name.split(' - ');
      name = parts[0].trim();
      company = parts[1].trim();
    }

    // Extract address components
    let street = pdfData.CustomerAddress?.street || '';
    let city = pdfData.CustomerAddress?.city || '';
    let zipCode = pdfData.CustomerAddress?.zipCode || '';

    // Sometimes the address might be combined in the street field
    // Try to extract city from the street if it contains multiple parts
    if (street && street.includes(',')) {
      const addressParts = street.split(',');
      street = addressParts[0].trim();
      if (addressParts[1]) {
        city = addressParts[1].trim();
      }
    }

    // Sometimes zipCode might contain city info like "26802 Moormerland"
    if (zipCode && zipCode.includes(' ')) {
      const zipParts = zipCode.split(' ');
      zipCode = zipParts[0].trim();
      if (zipParts[1] && !city) {
        city = zipParts.slice(1).join(' ').trim();
      }
    }

    return {
      name: name.trim(),
      company: company.trim(),
      street: street.trim(),
      city: city.trim(),
      zipCode: zipCode.trim(),
      licensePlate:  pdfData.LicensePlate,
    };
  };

  // Initialize form values from PDF parser data
  const customerInfo = parseCustomerInfo(pdfData);

  let formValues = {
    nameValue: customerInfo.name || '',
    companyValue: customerInfo.company || '',
    streetValue: customerInfo.street || '',
    cityValue: customerInfo.city || '',
    zipValue: customerInfo.zipCode || '',
    email: '',
    iban: '',
    accountHolder: customerInfo.name || '',
    licensePlate: customerInfo.licensePlate || '',
  };


  const checkIban = async () => {
    if (formValues.iban) {
      formValues.iban = formValues.iban.replace(/\s/g, '');
    }
    if (
      formValues.iban.length === 21 ||
      formValues.iban.length === 22
    ) {
      const iban = formValues.iban;

      // check if the iban is only from CH, AT, FR, LU, BE, NL
      const countryCode = iban.substring(0, 2);
      debugLog({ countryCode });
      if (!ALLOWED_IBAN_COUNTRIES.includes(countryCode.toUpperCase())) {
        ibanInput.setCustomValidity(
          $_(
            'This IBAN number does not meet the requirements of our terms and conditions. An account in the Federal Republic of Germany must be specified. Please provide a correct IBAN number.',
          ),
        );
        form.reportValidity();
        return;
      }

      const response = await fetch(
        `https://openiban.com/validate/${iban}?getBIC=true&validateBankCode=true`,
      );
      const data = await response.json();
      debugLog({ data });
      if (data.valid) {
        bankDetails = data.bankData;
        ibanInput.setCustomValidity('');
      } else {
        ibanInput.setCustomValidity($_('Invalid IBAN'));
        form.reportValidity();
      }
    } else {
      ibanInput.setCustomValidity(
        $_('The IBAN number must consist of 22 characters'),
      );
      form.reportValidity();
    }
  };

  const createPurchasereceipts = async () => {
    const dealershipRaw = await identifyDealership(dealer, dealershipLocation, zipCode);

    try {
      await handleFetchRetry(() =>
        fetch(`${URL}/purchasereceipts/${receipt.id}`, {
          method: 'PATCH',
          headers: {
            Accept: 'application/json, text/plain',
            'Content-Type': 'application/json;charset=UTF-8',
            'X-Custom-Header': 'mb-cashback-2025-header',
          },
          body: JSON.stringify({
            // partsCount: `${receipt.partsCount}`,
            // partsId: JSON.stringify(selectedParts.map((s) => s.id)),
            // partsIdentified: JSON.stringify(selectedParts.map((s) => s.key)),
            // partsOverrride: +isOverrided,
            // purchaseDate: new Date(receipt.purchaseDate),
            // purchaseDate: receipt.purchaseDate,
            // purchaseDateOverride: +(invoiceDate !== receipt.invoiceDate),
            dealershipsId: dealershipInfo?.value,
            dealerOverride: +(dealershipRaw?.id !== dealershipInfo?.value),
            licensePlate: formValues.licensePlate,
            // switch: switchValue,
            // switchOverride:
            //   "" + +(switchValue !== response?.["montage"]?.value),
            // storage: storageKind,
            // storageOverride:
            //   "" + +(storageKind !== response?.["einlagerung"]?.value),
          }),
        }),
      );
    } catch (error) {
      debugLog({ error });
    }

    localStorage.setItem(
      'receipt',
      JSON.stringify({
        ...receipt,
        // partsOverride: isOverrided,
        // partsCount: partsCount.toString(),
        // partsId: JSON.stringify(selectedParts.map((s) => s.id)),
        // partsIdentified: JSON.stringify(selectedParts.map((s) => s.key)),
        // storage: storageKind,
        // storageOverride:
        //   "" + +(storageKind !== response?.["einlagerung"]?.value),
      }),
    );
  };

   const createPersonalInformation = async () => {
    // read and update customer process status
    const item = localStorage.getItem('customerProcess');
    const customerProcess = item !== null ? JSON.parse(item as string) : null;

    // Switch back to real backend calls
    if (!purchasePaybackId) {
      debugLog('Must start the purchase process first');
      return;
    }

    const payload = {
      address: {
        city: formValues.cityValue,
        street: formValues.streetValue,
        zip: formValues.zipValue,
      },
      email: formValues.email,
      name: formValues.nameValue,
      company: formValues.companyValue,
      iban: formValues.iban.replace(/\s/g, ''),
      ibanVerified: '1',
      ibanAccountHolder: formValues.accountHolder,
    };

    const resp = await handleFetchRetry(() =>
      fetch(`${URL}/personalinformations`, {
        method: 'POST',
        headers: {
          Accept: 'application/json, text/plain',
          'Content-Type': 'application/json;charset=UTF-8',
          'X-Custom-Header': 'mb-cashback-2025-header',
        },
        body: JSON.stringify(payload),
      }),
    );

    const personalInformation = await resp.json();

    localStorage.setItem(
      'personalInformation',
      JSON.stringify(personalInformation),
    );

    await handleFetchRetry(() =>
      fetch(`${URL}/purchasepaybacks/${purchasePaybackId}`, {
        method: 'PATCH',
        headers: {
          Accept: 'application/json, text/plain',
          'Content-Type': 'application/json;charset=UTF-8',
          'X-Custom-Header': 'mb-cashback-2025-header',
        },
        body: JSON.stringify({
          personalinformationId: personalInformation.id,
          ...(getStoredReferer() ? { referer: getStoredReferer() } : {}),
        }),
      }),
    );

    await createPurchasereceipts();

    navigate('/step/4');
  };

  const checkValidity = async () => {
    if (formValues.iban) {
      formValues.iban = formValues.iban.replace(/\s/g, '');
    }
    ibanInput.setCustomValidity('');

    if (form.checkValidity()) {
      // test the iban number validity if the length is 21 or 22
      if (
        formValues.iban.length === 21 ||
        formValues.iban.length === 22
      ) {
        const iban = formValues.iban;

        // check if the iban is only from CH, AT, FR, LU, BE, NL
        const countryCode = iban.substring(0, 2);
        debugLog({ countryCode });
        if (!ALLOWED_IBAN_COUNTRIES.includes(countryCode.toUpperCase())) {
          ibanInput.setCustomValidity(
            $_(
              'This IBAN number does not meet the requirements of our terms and conditions. An account in the Federal Republic of Germany must be specified. Please provide a correct IBAN number.',
            ),
          );
          form.reportValidity();
        }

        const response = await fetch(
          `https://openiban.com/validate/${iban}?getBIC=true&validateBankCode=true`,
        );
        const data = await response.json();
        debugLog({ data });
        if (data.valid) {
          bankDetails = data.bankData;
          ibanInput.setCustomValidity('');
        } else {
          ibanInput.setCustomValidity($_('Invalid IBAN'));
          form.reportValidity();
        }
      } else {
        ibanInput.setCustomValidity(
          $_('The IBAN number must consist of 22 characters'),
        );
        form.reportValidity();
      }
    } else {
      form.reportValidity();
    }
  };

  const showMessageForInput = (e: any) => {
    if (e.target.value.length) e.target.setCustomValidity('');
    else e.target.setCustomValidity($_('Fülle dieses Feld aus.'));
  };

  const showMessageForIban = (e: any) => {
    if (!e.target.value.length)
      e.target.setCustomValidity($_('Please tell us your IBAN number here.'));
  };
</script>

<div class="flex flex-1 flex-col space-y-6">
  <div>
    <h1 class="font-CorpoA text-3xl text-black">{$_('Personal Information')}</h1>
  </div>

  <div>
    <p class="font-sans text-sm text-black">
      {$_('personal-info-intro')}
    </p>
    <br />

    <form action="" class="grid gap-3" bind:this={form}>
      <!-- Name -->
      <div class="form-field">
        <label class="form-label required" for="name">
          {$_('NAME')}
        </label>
        <input
          id="name"
          required
          type="text"
          class="form-input"
          bind:value={formValues.nameValue}
          on:invalid={showMessageForInput}
        />
      </div>

      <!-- Company -->
      <div class="form-field">
        <label class="form-label" for="company">
          {$_('COMPANY')}
        </label>
        <input
          id="company"
          type="text"
          class="form-input"
          bind:value={formValues.companyValue}
        />
      </div>

      <!-- Street -->
      <div class="form-field">
        <label class="form-label required" for="street">
          {$_('STREET')}
        </label>
        <input
          id="street"
          required
          type="text"
          class="form-input"
          bind:value={formValues.streetValue}
          on:invalid={showMessageForInput}
        />
      </div>

      <div class="form-row gap-3">
        <!-- Zip Code -->
        <div class="form-field !w-1/4 !flex-auto">
          <label class="form-label required" for="zip">
            {$_('ZIP CODE')}
          </label>
          <input
            id="zip"
            required
            type="text"
            class="form-input"
            bind:value={formValues.zipValue}
            on:invalid={showMessageForInput}
          />
        </div>

        <!-- City -->
        <div class="form-field !w-3/4 !flex-auto">
          <label class="form-label required" for="city">
            {$_('CITY')}
          </label>
          <input
            id="city"
            required
            type="text"
            class="form-input"
            bind:value={formValues.cityValue}
            on:invalid={showMessageForInput}
          />
        </div>
      </div>

      <div class="form-field">
        <!-- Email -->
        <label class="form-label required" for="email">
          {$_('EMAIL')}
        </label>
        <input
          id="email"
          required
          type="email"
          class="form-input"
          pattern={"[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?"}
          on:invalid={showMessageForInput}
          bind:value={formValues.email}
        />
      </div>

      <!-- Account Holder -->
      <div class="form-field">
        <!-- MBD-592: was using literal string 'Name des Konto-Inhabers' as the i18n key, which doesn't exist
             in translation.json, so it silently fell back to that (mis-hyphenated) raw text -->
        <label class="form-label required" for="accountHolder">
          {$_('Account Holder Name')}
        </label>
        <input
          id="accountHolder"
          required
          type="text"
          class="form-input"
          bind:value={formValues.accountHolder}
          on:invalid={showMessageForInput}
        />
      </div>
      <!-- IBAN -->
      <div class="form-field">
        <label class="form-label required" for="iban">
          {$_('IBAN')}
        </label>
        <input
          id="iban"
          required
          type="text"
          class="form-input"
          bind:value={formValues.iban}
          bind:this={ibanInput}
          on:input={() => {
            checkIban();
          }}
          on:blur={() => {
            checkIban();
          }}
          on:invalid={showMessageForIban}
        />
      </div>

      {#if bankDetails !== null}
        <div class="form-row">
          <div class="form-field">
            <div class="form-label">
              {$_('BANK NAME')}
            </div>
            <div class="data-field">
              {bankDetails.name}
            </div>
          </div>
          <div class="form-field">
            <div class="form-label">
              {$_('BIC')}
            </div>
            <div class="data-field">
              {bankDetails.bic}
            </div>
          </div>
        </div>
      {/if}

      <!-- Dealer -->

      <div class="form-field">
        <div class="form-label">
          {#if showDealershipDropdown}
            {$_('DEALER')}
          {/if}
        </div>
        <DealerDropdown
          bind:dealershipInfo
          {dealer}
          {showDealershipDropdown}
          {dealershipLocation}
          {zipCode}
        />
      </div>

      <!-- hidden purchasepaybackID -->

      <div class="form-field">
        <div class="form-label"></div>
        <div class="form-field !hidden">
          <div class="data-field !hidden" id="hidden-purchasepaybackid-info">
            {purchasePaybackId}
          </div>
        </div>
      </div>
    </form>
  </div>

  <!-- Submit -->

  <div class="flex justify-end py-4 font-sans">
    <div class="flex w-full justify-between">
      <p class="text-cubiq-dark-blue">
        * {$_('required')}
      </p>
      <button
        class="btn-primary mt-4"
        type="submit"
        data-umami-event="next button on personal information clicked"
        data-umami-event-page="/step/3"
        on:click|preventDefault={async () => {
          await checkValidity();

          if (form.checkValidity()) {
            // try to create the personal information in the backend
            await createPersonalInformation();
            // redirect to step 4
          }
        }}>{$_('Next')}</button
      >
    </div>
  </div>
</div>
